import { useEffect, useState } from "react";
import "./App.css";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

// Import page containers
import LoginPage from "./pages/LoginPage/LoginPage";
import MainPage from "./pages/MainPage/MainPage";
import FormPage from "./pages/FormPage/FormPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import TopBar from "./components/TopBar/TopBar";
import MaintenancePage from "./pages/MaintenancePage/MaintenancePage";

// Import userContext
import { useUserContext } from "./contexts/UserContext";
import config from "./config/config";
import DoctorLandingPage from "./pages/DoctorPages/DoctorLandingPage/DoctorLandingPage";
import DoctorApprovalPage from "./pages/DoctorPages/DoctorApprovalPage/DoctorApprovalPage";
import AdminLandingPage from "./pages/AdminPages/AdminLandingPage/AdminLandingPage";
import { useRoutesContext } from "./contexts/RoutesContext";
import { useClearContextsContext } from "./contexts/ClearContextsContext";
import { Unit } from "./contexts/interfaces";
import { getSystemConfig } from "./scripts/dataHandlers";

type AppProps = {
  pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
  // User from context used for viewing/hiding topBar
  const { user } = useUserContext();
  const { routes, setRoutes } = useRoutesContext();
  const { clearContexts } = useClearContextsContext();
  const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [previousPath, setPreviousPath] = useState<string>();
  const [redirectedForLogin, setRedirectedForLogin] = useState<boolean>(false);
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false); // Database-based maintenance mode
  const [maintenanceModeLoading, setMaintenanceModeLoading] = useState<boolean>(true);

  // Init program
  useEffect(() => {
    // Set page title from config file
    document.title = config.pageTitle;
    // Set routes into a context
    setRoutes({ ...config.routes });
  }, []);

  // Fetch database-based maintenance mode status (only after user is logged in)
  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      // Only fetch if user is logged in (requires authentication)
      if (!user) {
        setMaintenanceModeLoading(false);
        return;
      }

      try {
        setMaintenanceModeLoading(true);
        const response = await getSystemConfig("maintenanceMode");
        if (response?.data?.result?.value) {
          const value = response.data.result.value.toLowerCase();
          setMaintenanceMode(value === "true");
        } else {
          setMaintenanceMode(false);
        }
      } catch (error) {
        console.error("Failed to fetch maintenance mode:", error);
        // If we can't fetch, default to false (maintenance mode off)
        setMaintenanceMode(false);
      } finally {
        setMaintenanceModeLoading(false);
      }
    };

    fetchMaintenanceMode();
  }, [user]);

  // Handle initial login redirect
  useEffect(() => {
    if (!user && pathname !== "/") {
      // Clear contexts, put pathname to previousPath state and navigate user to login view
      clearContexts();
      setPreviousPath(pathname);
      setRedirectedForLogin(true);
      navigate("/");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user]);

  // Handle post-login navigation
  useEffect(() => {
    // Only run this effect when the user changes from null to a value (user logged in)
    if (user && redirectedForLogin && previousPath) {
      // User has logged in and we have a saved path, go there
      setRedirectedForLogin(false);
      navigate(previousPath);
      return;
    }

    // Handle the case when user logs in directly from the login page (not redirected from another page)
    if (user && pathname === "/" && !redirectedForLogin) {
      // Admins should go to their normal view (doctor or main), not admin view by default
      if (user.isDoctor) {
        navigate(`/${routes.doctorMain}`);
      } else {
        navigate(`/${routes.main}`);
      }
      return;
    }

    // Handle regular routing rules when not recovering from a login redirect
    if (user && !redirectedForLogin) {
      // Redirect non-admin users away from admin page
      if (pathname.includes(routes.admin) && !user.isAdmin) {
        // User is trying to access admin page but is not an admin - redirect to appropriate page
        if (user.isDoctor) {
          navigate(`/${routes.doctorMain}`);
        } else {
          navigate(`/${routes.main}`);
        }
        return;
      }

      // Don't auto-redirect admins to admin page - they should navigate there manually via dropdown
      if (
        user.isDoctor &&
        !pathname.includes(routes.doctorMain) &&
        !pathname.includes(routes.doctorApproval) &&
        !pathname.includes(routes.admin) &&
        pathname !== "/"
      ) {
        // Navigate to doctor main page if the user is logged in as doctor but not already on a doctor page
        navigate(`/${routes.doctorMain}`);
      } else if (
        !user.isDoctor &&
        !pathname.includes(routes.main) &&
        !pathname.includes(routes.form) &&
        !pathname.includes(routes.profile) &&
        !pathname.includes(routes.admin) &&
        pathname !== "/"
      ) {
        // Navigate to main page if the user is logged in but not already on a normal user page
        navigate(`/${routes.main}`);
      }
    }
  }, [user, pathname, routes, previousPath, redirectedForLogin, navigate]);

  // FIRST CHECK: System-wide maintenance mode (config-based) - blocks EVERYONE including admins
  // This is checked before login and prevents anyone from accessing the app
  if (config.systemMaintenanceMode) {
    return (
      <MsalProvider instance={pca}>
        <div className="App">
          <TopBar />
          <MaintenancePage />
        </div>
      </MsalProvider>
    );
  }

  // SECOND CHECK: Database-based maintenance mode (only checked after login)
  // Show loading state while checking database maintenance mode (only if user is logged in)
  if (user && maintenanceModeLoading) {
    return (
      <MsalProvider instance={pca}>
        <div className="App">
          <TopBar />
          <div style={{ padding: "20px", textAlign: "center" }}>Ladataan...</div>
        </div>
      </MsalProvider>
    );
  }

  // If database-based maintenance mode is on and user is logged in but not a superadmin, show maintenance page
  // Superadmins can bypass database-based maintenance mode
  if (maintenanceMode && user && !user.isSuperAdmin) {
    return (
      <MsalProvider instance={pca}>
        <div className="App">
          <TopBar />
          <MaintenancePage />
        </div>
      </MsalProvider>
    );
  }

  // If user is set show routes depending on if user is admin, doctor, or regular user.
  // If user isn't set, render only login route.
  if (user) {
    console.log("===========LOGGEDIN");
    return (
      <MsalProvider instance={pca}>
        <div className="App">
          <TopBar />
          <Routes>
            <Route path="/" element={<LoginPage />} />

            {/* Admin route - available to all admins */}
            {user?.isAdmin && (
              <Route
                path={`/${config.routes.admin}`}
                element={<AdminLandingPage />}
              />
            )}

            {/* Doctor routes - available to doctors (including admin doctors) */}
            {user?.isDoctor ? (
              <>
                <Route
                  path={`/${config.routes.doctorMain}`}
                  element={<DoctorLandingPage />}
                />
                <Route
                  path={`/${config.routes.doctorApproval}/:empId/:formId`}
                  element={<DoctorApprovalPage />}
                />
              </>
            ) : (
              <>
                {/* Regular user routes - available to non-doctors (including admin non-doctors) */}
                <Route
                  path={`/${config.routes.main}`}
                  element={
                    <MainPage
                      selectedUnit={selectedUnit}
                      setSelectedUnit={setSelectedUnit}
                    />
                  }
                />
                <Route path={`/${config.routes.form}`} element={<FormPage />} />
                <Route
                  path={`/${config.routes.form}/:empUnit/:empId/:formId`}
                  element={<FormPage />}
                />
                <Route
                  path={`/${config.routes.form}/:empUnit/:empId`}
                  element={<FormPage />}
                />
                <Route
                  path={`/${config.routes.profile}/:empUnit/:empId`}
                  element={<ProfilePage />}
                />
              </>
            )}
          </Routes>
        </div>
      </MsalProvider>
    );
  } else {
    console.log("===========NOT LOGGEDIN");
    return (
      <MsalProvider instance={pca}>
        <div className="App">
          <TopBar />
          <Routes>
            <Route path="/" element={<LoginPage />} />
          </Routes>
        </div>
      </MsalProvider>
    );
  }
}

export default App;
